let ProgramUtil = require("../util/ProgramUtil");

let {
  Table,
  Drop,
  IconButton,
  Blip
} = mc.widgets;

let {
  GREEN,
  BLUE,
  PINK,
  ORANGE,
  LIGHT_GRAY,
  NAVY
} = mc.ui.colors;

let SubjectTable = function (props) {
  let { study, subjects, cols, sortKey, sortDirection, onSort } = props;

  let columns = SubjectTable.columns(study).filter(function (col) {
    return col.prop === "displayName" || cols.includes(col.prop);
  });

  if (!columns.find((c) => c.prop === sortKey)) {
    sortKey = "displayName";
    sortDirection = 1;
  }

  return (
    <Table
      id="rc-subject-table"
      data={subjects}
      rowKey="id"
      align="middle"
      cols={columns}
      sort={sortKey}
      direction={sortDirection}
      onSort={onSort}/>
  );
};

SubjectTable.columns = function (study) {
  let hasRemoteConfigs = !!(study.deviceConfigs || []).find((cfg) => cfg.mode === "UNSUPERVISED");

  let cols = [
    {
      prop: "displayName",
      label: tx("subject_id"),
      content: function (subject) {
        let href = mc.router.resolve("SUBJECT_DETAIL_VIEW", { studyId: subject.studyId, subjectId: subject.id });

        return (
          <Blip color={subject.isHidden ? LIGHT_GRAY : NAVY}>
            <a href={href} data-hidden={subject.isHidden}>
              {subject.displayName}
            </a>
          </Blip>
        );
      },
      required: true,
      sort: mc.utils.sortIgnoreCase((subject) => subject.displayName)
    },
    {
      prop: "enrolled",
      label: tx("created"),
      content: function (subject) {
        return mc.format.date(subject.createdTs);
      },
      sort: function (a, b) {
        return a.createdTs - b.createdTs;
      }
    },
    {
      prop: "gender",
      label: tx("gender"),
      content: function (subject) {
        let label = subject.gender ? tx("gender_abbr." + subject.gender) : "-";
        let colors = {
          "MALE": BLUE,
          "FEMALE": PINK
        };

        return (
          <Blip color={colors[subject.gender] || LIGHT_GRAY}>
            {label}
          </Blip>
        );
      },
      sort: ["gender", "age"]
    },
    {
      prop: "age",
      label: tx("age"),
      content: function (subject) {
        return subject.age ? subject.age.toString() : "-";
      },
      sort: ["age", "gender"]
    },
    {
      prop: "weight",
      label: tx("weight"),
      content: function (subject) {
        return subject.weight ? mc.format.weight(subject.weight) : "-";
      },
      sort: true
    },
    {
      prop: "height",
      label: tx("height"),
      content: function (subject) {
        return subject.height ? mc.format.height(subject.height) : "-";
      },
      sort: true
    },
    {
      prop: "bmi",
      label: tx("bmi"),
      content: function (subject) {
        return subject.weight && subject.height ? mc.format.bmi(subject.weight, subject.height) : "-";
      },
      sort: function (s1, s2) {
        let a = (s1.weight / (s1.height ** 2)) || 0;
        let b = (s2.weight / (s2.height ** 2)) || 0;

        return a > b ? -1 : 1;
      }
    },
    {
      prop: "compliancePct",
      label: tx("pct_tasks"),
      content: function (subject) {
        if (!study.program) {
          return "N/A";
        }

        return !isNaN(subject.compliancePct) ? mc.format.percent(subject.compliancePct) : "-";
      },
      sort: true
    },
    {
      prop: "programPct",
      label: tx("pct_program"),
      content: function (subject) {
        if (!study.program) {
          return "N/A";
        }

        let info = ProgramUtil.info(study.program, subject.programStartTs, subject.timezone);
        let pct = Math.min(1, info.currentDay / info.totalDays);

        return mc.format.percent(pct);
      },
      sort: ["programStartTs"]
    },
    {
      prop: "lastStartTs",
      label: tx("last_recording"),
      content: function (subject) {
        let subjectId = subject.id;
        let studyId = subject.studyId;
        let startTs = subject.lastStartTs;
        let date = mc.format.date(startTs, subject.timezone);

        if (startTs) {
          return (
            <a href={mc.router.resolve("RECORDING_DATA_VIEW", { studyId, subjectId, startTs })}>
              {date}
            </a>
          );
        }

        return "-";
      },
      sort: true
    },
    {
      prop: "durationMs",
      label: tx("data_hours"),
      content: function (subject) {
        if (subject.durationMs) {
          return mc.format.number(Math.ceil(subject.durationMs / mc.math.MILLIS_PER_HOUR));
        }

        return "-";
      },
      sort: true
    },
    {
      prop: "countDays",
      label: tx("usage_days"),
      content: function (subject) {
        if (subject.countDays) {
          return subject.countDays;
        }

        return "-";
      },
      sort: true
    },
    {
      prop: "analytics",
      label: tx("analytics"),
      content: function (subject) {
        let subjectId = subject.id;
        let studyId = subject.studyId;
        let href = mc.router.resolve("METRIC_LIST_VIEW", { studyId, subjectId });

        if (subject.hasAnalytics) {
          return <a href={href}>{tx("analytics")}</a>;
        }

        return "-";
      },
      sort: ["hasAnalytics", "lastStartTs"]
    },
    {
      prop: "messageCount",
      label: tx("messages"),
      content: function (subject) {
        let href = mc.router.resolve("SUBJECT_MESSAGES_VIEW", { studyId: subject.studyId, subjectId: subject.id });
        let n = subject.messageCount;

        if (!hasRemoteConfigs) {
          return "N/A";
        }

        return (
          <Blip color={n > 0 ? GREEN : LIGHT_GRAY}>
            { n > 0 ? <a href={href}>{n}</a> : n }
          </Blip>
        );
      },
      sort: true
    },
    {
      prop: "faults",
      label: tx("equip_faults"),
      content: function (subject) {
        let n = subject.faultCount;

        if (!hasRemoteConfigs) {
          return "N/A";
        }

        return (
          <Blip color={n > 0 ? ORANGE : LIGHT_GRAY}>
            {n}
          </Blip>
        );
      },
      sort: true
    }
  ];

  (study.subjectMetadataFields || []).forEach(function (field) {
    let prop = field.displayName;

    let sortAlpha = mc.utils.sortIgnoreCase(function (subject) {
      let field = subject.metadataFields.find((f) => f.displayName === prop);

      return field ? field.value : "";
    });

    let sortNumeric = function (a, b) {
      return a > b ? -1 : 1;
    };

    cols.push({
      prop: prop,
      label: prop,
      content: function (subject) {
        let field = subject.metadataFields.find((f) => f.displayName === prop);

        return field ? field.value : "-";
      },
      sort: field.type === "NUMERIC" ? sortNumeric : sortAlpha
    });
  });

  return cols;
};

SubjectTable.Drop = function (props) {
  let { study, selected = [], onSelect } = props;

  let onChange = function (evt) {
    let { value, checked } = evt.target;

    if (checked) {
      selected.push(value);
      selected = mc.utils.distinct(selected);
    }
    else {
      selected = selected.filter((col) => col !== value);
    }

    onSelect(selected);
  };

  let columns = SubjectTable.columns(study).filter((col) => !col.required);

  return (
    <Drop align="right">
      <IconButton icon="configure" label={tx("customize")} scale={1.2}/>
      <ul className="rc-subject-list-view-picker mc-drop-content">
        {columns.map((col, i) => {
          let checked = selected.includes(col.prop);

          return (
            <li key={col.prop}>
              <label>
                <input type="checkbox" checked={checked} value={col.prop} onChange={onChange}/>
                <span>{col.label}</span>
              </label>
            </li>
          );
        })}
      </ul>
    </Drop>
  );
};

SubjectTable.DEFAULT_COL_PREF = [
  "enrolled",
  "gender",
  "age",
  "weight",
  "height",
  "compliancePct",
  "messageCount"
];

module.exports = SubjectTable;
